import { defineRule, configure } from 'vee-validate'
import { required, email, alpha, regex, numeric, max } from '@vee-validate/rules'
import i18n from './i18n'

// Custom generateMessage function to use i18n
const generateMessage = ({ field, rule, values }) => {
  // Remove numeric suffix (e.g., "-0", "-1", etc.) from the field name
  const baseField = field.replace(/-\d+$/, '')
  // Override the field name
  if (!values) values = {} // Ensure values is defined
  values._field_ = i18n.global.t(`fields.${baseField}`)
  return i18n.global.t(`validation.${rule.name}`, values)
}

// Configure vee-validate with the custom message generator
configure({
  generateMessage,
  bails: false,
})

// Install validation rules
defineRule('required', required)
defineRule('alpha', alpha)
defineRule('regex', regex)
defineRule('email', email)
defineRule('numeric', numeric)
defineRule('max', max)

defineRule('min', (value, [limit]) => {
  if (!value || !value.length) {
    return true
  }
  if (value.length < limit) {
    return i18n.global.t('validation.min', { limit: limit })
  }
  return true
})

defineRule('checkSpecial', (value) => {
  if (value) {
    return /^[^<;&]+$/.test(value) || i18n.global.t('validation.checkSpecial')
  } else {
    return true
  }
})

defineRule('checkDocumentNumber', (value, [target]) => {
  if (value) {
    const uppercasedVal = value.toUpperCase()
    let letters = uppercasedVal.length == 9 ? uppercasedVal.substring(0, 2) : uppercasedVal.substring(0, 1)
    let digits = uppercasedVal.length == 9 ? uppercasedVal.substring(2) : uppercasedVal.substring(1)
    const reg = /^\d+$/
    const lengthTest = value.length == 9
    let digitsTest = reg.test(digits)

    if (target == 'ID-card') {
      if (
        (letters == 'N' && uppercasedVal.length == 8 && reg.test(digits)) ||
        (['NA', 'UA'].includes(letters) && uppercasedVal.length == 9 && reg.test(digits))
      ) {
        return i18n.global.t('validation.documentNumberExtra')
      } else {
        let lettersTest = ['AA', 'AB', 'AC', 'EA', 'EB', 'EC'].includes(letters)
        return lettersTest && lengthTest && digitsTest ? true : i18n.global.t('validation.documentNumber')
      }
    } else if (target == 'Estonian Passport') {
      let lettersTest = ['KD', 'KE', 'KF'].includes(letters)
      return lettersTest && lengthTest && digitsTest ? true : i18n.global.t('validation.documentNumber')
    } else if (target == 'Residence permit card') {
      let lettersTest = ['PB', 'PC', 'PD', 'PE', 'BB', 'BC', 'BD', 'BE'].includes(letters)
      return lettersTest && lengthTest && digitsTest ? true : i18n.global.t('validation.documentNumber')
    }
  }
})

defineRule('checkOver14', (value) => {
  if (value) {
    let digitTest = /^(3|4|5|6)[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[1-2][0-9]|3[0-1])[0-9]{4}$/.test(value)
    const birthYear = parseInt(value.substr(1, 2), 10)
    const birthMonth = parseInt(value.substr(3, 2), 10)
    const birthDay = parseInt(value.substr(5, 2), 10)
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const currentDay = new Date().getDate()
    let age = currentYear - birthYear
    if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
      age--
    }
    let ageString = age.toString()
    let ageTest14 = ageString.slice(2) >= 14
    let ageTest18 = ageString.slice(2) <= 17

    if (digitTest) {
      if (ageTest14 && ageTest18) {
        return true
      } else if (!ageTest18) {
        return i18n.global.t('validation.over18')
      } else {
        return i18n.global.t('validation.over14')
      }
    }
  }
})

defineRule('checkLess14', (value) => {
  if (value) {
    let digitTest = /^(3|4|5|6)[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[1-2][0-9]|3[0-1])[0-9]{4}$/.test(value)
    const birthYear = parseInt(value.substr(1, 2), 10)
    const birthMonth = parseInt(value.substr(3, 2), 10)
    const birthDay = parseInt(value.substr(5, 2), 10)
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const currentDay = new Date().getDate()
    let age = currentYear - birthYear
    if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
      age--
    }
    let ageString = age.toString()
    let ageTest = ageString.slice(2) < 14
    return digitTest && ageTest ? true : i18n.global.t('validation.less14')
  }
})

defineRule('checkOver18', (value) => {
  if (value) {
    let digitTest = /^(3|4|5|6)[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[1-2][0-9]|3[0-1])[0-9]{4}$/.test(value)
    const birthYear = parseInt(value.substr(1, 2), 10)
    const birthMonth = parseInt(value.substr(3, 2), 10)
    const birthDay = parseInt(value.substr(5, 2), 10)
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const currentDay = new Date().getDate()
    let age = currentYear - birthYear
    if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
      age--
    }
    let ageString = age.toString()
    let ageTest18 = ageString.slice(2) >= 18
    if (digitTest) {
      return ageTest18 ? true : i18n.global.t('validation.less18')
    }
  }
})
